import { useTranslation } from "react-i18next";
import { SafeText } from "@/shared/components/SafeText";
import { sanitizeDisplayText } from "@/shared/lib/safe-text";
import { Badge } from "@/shared/components/ui/badge";
import type { StaffAssignmentRow } from "../staff/types";

interface AssignmentListProps {
  /** Accessible name of the list (which organization these belong to). */
  readonly label: string;
  readonly rows: readonly StaffAssignmentRow[];
  /** Bulk-selection checkboxes are shown to admins only. */
  readonly canManage: boolean;
  readonly selected: ReadonlySet<string>;
  readonly onToggle: (entityId: string) => void;
}

/** Assigned locations of a STAFF membership (active and inactive), selectable for admins. */
export function AssignmentList({
  label,
  rows,
  canManage,
  selected,
  onToggle,
}: AssignmentListProps) {
  const { t } = useTranslation();
  return (
    <ul aria-label={label} className="divide-y divide-border rounded-md border border-border">
      {rows.map((r) => (
        <li
          key={`${r.kind}:${r.entityId}`}
          data-testid={`staff-assignment-${r.entityId}`}
          className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm"
        >
          {canManage && (
            <input
              type="checkbox"
              data-testid={`staff-select-${r.entityId}`}
              aria-label={t("users.staff.selectRow", { name: sanitizeDisplayText(r.entityName) })}
              checked={selected.has(r.entityId)}
              onChange={() => onToggle(r.entityId)}
              className="h-4 w-4 accent-primary"
            />
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium">
              <SafeText>{r.entityName}</SafeText>
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {t(`users.staff.kind.${r.kind}`)}
            </p>
          </div>
          <Badge variant={r.isActive ? "success" : "secondary"}>
            {r.isActive ? t("users.staff.status.active") : t("users.staff.status.inactive")}
          </Badge>
        </li>
      ))}
    </ul>
  );
}
